import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import {
  AdminNotificationResponse,
  AdminNotificationsListParams,
  AdminNotificationsListResponse,
  AdminNotificationsMarkAllReadResponse,
  AdminNotificationsUnreadCountResponse,
} from '@oneComply/core/models/notification.model';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class AdminNotificationsService {
  private readonly http = inject(HttpClient);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);

  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  list(params: AdminNotificationsListParams = {}): Observable<AdminNotificationsListResponse> {
    let httpParams = new HttpParams();

    if (params.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }
    if (params.offset !== undefined) {
      httpParams = httpParams.set('offset', params.offset.toString());
    }
    if (params.unread_only !== undefined) {
      httpParams = httpParams.set('unread_only', params.unread_only ? 'true' : 'false');
    }

    return this.http.get<AdminNotificationsListResponse>(
      `${environment.apiUrl}/admin/notifications`,
      { params: httpParams },
    );
  }

  getUnreadCount(): Observable<AdminNotificationsUnreadCountResponse> {
    return this.http
      .get<AdminNotificationsUnreadCountResponse>(
        `${environment.apiUrl}/admin/notifications/unread-count`,
      )
      .pipe(tap((response) => this.unreadCountSubject.next(response.data.count)));
  }

  refreshUnreadCount(): void {
    this.getUnreadCount().subscribe();
  }

  markAsRead(uuid: string): Observable<AdminNotificationResponse> {
    return this.http
      .patch<AdminNotificationResponse>(
        `${environment.apiUrl}/admin/notifications/${uuid}/read`,
        {},
      )
      .pipe(tap(() => this.refreshUnreadCount()));
  }

  markAllAsRead(): Observable<AdminNotificationsMarkAllReadResponse> {
    return this.http
      .patch<AdminNotificationsMarkAllReadResponse>(
        `${environment.apiUrl}/admin/notifications/read-all`,
        {},
      )
      .pipe(tap(() => this.unreadCountSubject.next(0)));
  }
}
